import React, { useEffect, useMemo, useState } from 'react';
import {
  Search,
  RefreshCw,
  AlertTriangle,
  FileText,
  Boxes,
  Wallet,
  Calendar,
  Download,
  Filter,
} from 'lucide-react';
import { fetchDigitalJobReport } from '../../services/digitalJobService';
import { useIsCompactUser } from '../../helpers/currentUser';

const COLORS = {
  primary: '#a94007',
  accent: '#f97316',
  ink: '#1e293b',
  sub: '#64748b',
  border: '#e2e8f0',
  bgSoft: '#f8fafc',
};

const rupiah = (v) =>
  'Rp ' + Math.round(Number(v) || 0).toLocaleString('id-ID');

const fmtNum = (v) => (Number(v) || 0).toLocaleString('id-ID');

// Default range: full year 2026 (data starts in 2026 per the source system).
const todayISO = () => new Date().toISOString().slice(0, 10);

const KpiCard = ({ icon: Icon, label, value, hint, tone = 'default' }) => {
  const tones = {
    default: { bg: '#fff', color: COLORS.ink, iconBg: '#fff7ed', iconColor: COLORS.accent },
    warning: { bg: '#fffbeb', color: '#92400e', iconBg: '#fef3c7', iconColor: '#d97706' },
    danger: { bg: '#fef2f2', color: '#991b1b', iconBg: '#fee2e2', iconColor: '#dc2626' },
  };
  const t = tones[tone] || tones.default;
  return (
    <div
      style={{
        background: t.bg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '14px',
        padding: '16px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        flex: '1 1 200px',
        minWidth: 0,
      }}
    >
      <div
        style={{
          width: '42px',
          height: '42px',
          borderRadius: '12px',
          background: t.iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={20} color={t.iconColor} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: COLORS.sub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </div>
        <div style={{ fontSize: '20px', fontWeight: 800, color: t.color, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value}
        </div>
        {hint && <div style={{ fontSize: '11px', color: COLORS.sub, marginTop: '2px' }}>{hint}</div>}
      </div>
    </div>
  );
};

const DigitalJobReport = () => {
  const compact = useIsCompactUser();

  const [startDate, setStartDate] = useState('2026-01-01');
  const [endDate, setEndDate] = useState('2026-12-31');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [view, setView] = useState('jobs'); // 'jobs' | 'noStruk' | 'noJob'

  const load = async () => {
    if (!startDate || !endDate) return;
    if (startDate > endDate) {
      setError('Tanggal mulai tidak boleh melebihi tanggal akhir.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetchDigitalJobReport(startDate, endDate);
      setReport(res);
    } catch (e) {
      console.error(e);
      setError('Gagal memuat data. Periksa koneksi ke server sursys atau rentang tanggal.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  // Initial load.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(() => {
    if (!report) return [];
    const set = new Set();
    report.jobs.forEach((j) => j.categoryList.forEach((c) => set.add(c)));
    return Array.from(set).sort();
  }, [report]);

  const filteredJobs = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    return report.jobs.filter((j) => {
      if (categoryFilter !== 'ALL' && !j.categoryList.includes(categoryFilter)) return false;
      if (!q) return true;
      return (
        j.jobNo.toLowerCase().includes(q) ||
        j.strukno.toLowerCase().includes(q) ||
        (j.customer || '').toLowerCase().includes(q) ||
        (j.judul || '').toLowerCase().includes(q)
      );
    });
  }, [report, search, categoryFilter]);

  const datajobOn = !!report?.datajob?.available;

  const exportCsv = () => {
    if (!report) return;
    let header, lines;

    if (view === 'noStruk') {
      if (datajobOn) {
        header = ['No.Job', 'Wilayah', 'Tanggal', 'Pemesan', 'Kategori', 'Oplah', 'Judul', 'Status'];
        lines = [header.join(';')];
        report.merge.jobTanpaStruk.forEach((r) => {
          lines.push([r.jobNo, `"${r.wilayah}"`, r.tanggal, `"${(r.customer || '').replace(/"/g, "'")}"`, `"${r.kategori}"`, r.oplah, `"${(r.judul || '').replace(/"/g, "'")}"`, 'BELUM ADA STRUK'].join(';'));
        });
      } else {
        header = ['No.Job', 'Status', 'Job Terdekat (Pelanggan)'];
        lines = [header.join(';')];
        report.gap.missing.forEach((m) => {
          const dekat = m.prev ? `${m.prev.jobNo} (${m.prev.customer})` : (m.next ? `${m.next.jobNo} (${m.next.customer})` : '-');
          lines.push([m.jobNo, 'BELUM ADA STRUK (gap)', `"${dekat}"`].join(';'));
        });
      }
    } else if (view === 'noJob') {
      header = ['No.Struk', 'No.Job', 'Tanggal', 'Pemesan', 'Wilayah', 'Kategori', 'Total', 'Status'];
      lines = [header.join(';')];
      (report.merge?.strukTanpaJob || []).forEach((r) => {
        lines.push([`"${r.strukno}"`, r.jobNo, r.strukdate, `"${(r.customer || '').replace(/"/g, "'")}"`, `"${r.wilayah}"`, `"${r.category}"`, Math.round(r.total), 'STRUK TANPA JOB'].join(';'));
      });
    } else {
      header = ['No.Job', 'Tanggal Struk', 'No.Struk', 'Pemesan', 'Wilayah', 'Kategori', 'Judul Job', 'Qty', 'Total'];
      lines = [header.join(';')];
      filteredJobs.forEach((r) => {
        lines.push([r.jobNo, r.strukdate, `"${r.strukno}"`, `"${(r.customer || '').replace(/"/g, "'")}"`, `"${r.wilayah}"`, `"${r.category}"`, `"${(r.judul || '').replace(/"/g, "'")}"`, r.qty, Math.round(r.total)].join(';'));
      });
    }

    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `job-digital_${view}_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pad = compact ? '12px 16px' : '24px 32px';

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bgSoft, padding: pad }}>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 800, color: COLORS.ink, margin: 0, letterSpacing: '-0.025em' }}>
            Job Produksi Digital
          </h1>
          <p style={{ color: COLORS.sub, fontSize: '14px', marginTop: '4px', marginBottom: 0 }}>
            Pantau job toko digital (nomor depan <b>D</b>) dan temukan nomor job yang belum punya nomor struk.
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div
        style={{
          background: '#fff',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '14px',
          padding: '14px 16px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: '14px',
          marginBottom: '20px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: COLORS.sub }}>Tanggal Mulai</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '6px 10px' }}>
            <Calendar size={15} color={COLORS.sub} />
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              style={{ border: 'none', outline: 'none', fontSize: '13px', color: COLORS.ink, background: 'transparent' }} />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: COLORS.sub }}>Tanggal Akhir</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '6px 10px' }}>
            <Calendar size={15} color={COLORS.sub} />
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              style={{ border: 'none', outline: 'none', fontSize: '13px', color: COLORS.ink, background: 'transparent' }} />
          </div>
        </div>

        <button
          onClick={load}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: COLORS.primary, color: '#fff', border: 'none',
            borderRadius: '8px', padding: '9px 16px', fontSize: '13px', fontWeight: 600,
            cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
          }}
        >
          <RefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          {loading ? 'Memuat...' : 'Terapkan'}
        </button>

        <div style={{ flex: 1 }} />

        <button
          onClick={exportCsv}
          disabled={!report}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: '#fff', color: COLORS.ink, border: `1px solid ${COLORS.border}`,
            borderRadius: '8px', padding: '9px 14px', fontSize: '13px', fontWeight: 600,
            cursor: report ? 'pointer' : 'not-allowed', opacity: report ? 1 : 0.5,
          }}
        >
          <Download size={15} /> Export CSV
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: '12px', padding: '12px 16px', marginBottom: '18px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* KPIs */}
      {report && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '14px' }}>
          <KpiCard icon={Boxes} label="Total Job Digital" value={fmtNum(report.summary.totalJob)} hint={`${fmtNum(report.lineCount)} baris transaksi`} />
          <KpiCard icon={FileText} label="Total Struk" value={fmtNum(report.summary.totalStruk)} />
          <KpiCard icon={Wallet} label="Total Nilai" value={rupiah(report.summary.totalNilai)} />
          <KpiCard
            icon={AlertTriangle}
            label="Job Tanpa Struk"
            value={fmtNum(report.summary.totalMissing)}
            hint={datajobOn ? 'dari datajob (akurat)' : 'estimasi dari celah nomor'}
            tone={report.summary.totalMissing > 0 ? 'danger' : 'default'}
          />
          {datajobOn && (
            <KpiCard
              icon={AlertTriangle}
              label="Struk Tanpa Job"
              value={fmtNum(report.summary.strukTanpaJob)}
              hint="ada struk, tak ada di datajob"
              tone={report.summary.strukTanpaJob > 0 ? 'warning' : 'default'}
            />
          )}
        </div>
      )}

      {/* datajob connection banner */}
      {report && !datajobOn && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: '12px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <AlertTriangle size={15} style={{ marginTop: '1px', flexShrink: 0 }} />
          <span>
            Sumber <b>datajob</b> (produksi) belum terhubung, jadi "Job Tanpa Struk" memakai estimasi celah nomor & wilayah diturunkan dari nama customer.
            Hubungkan endpoint datajob agar wilayah + perbandingan akurat (lihat <code>fetchDigitalJobs</code> di gapfamilyAPI.js).
          </span>
        </div>
      )}

      {/* datajob connection success banner */}
      {report && datajobOn && (
        <div style={{ background: '#dcfce7', border: '1px solid #86efac', color: '#166534', borderRadius: '12px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>✓</span>
          <span>
            <b>Datajob terhubung!</b> Wilayah ditampilkan dengan warna hijau adalah data asli dari sistem produksi. Total {fmtNum(report.datajob.count)} job produksi digital dalam periode ini.
          </span>
        </div>
      )}

      {/* View toggle */}
      {report && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
          {[
            { k: 'jobs', label: `Daftar Job (${fmtNum(report.summary.totalJob)})`, active: COLORS.primary, bg: '#fff7ed' },
            { k: 'noStruk', label: `Job Tanpa Struk (${fmtNum(report.summary.totalMissing)})`, active: '#dc2626', bg: '#fef2f2' },
            ...(datajobOn ? [{ k: 'noJob', label: `Struk Tanpa Job (${fmtNum(report.summary.strukTanpaJob)})`, active: '#d97706', bg: '#fffbeb' }] : []),
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setView(t.k)}
              style={{
                padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${view === t.k ? t.active : COLORS.border}`,
                background: view === t.k ? t.bg : '#fff',
                color: view === t.k ? t.active : COLORS.sub,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !report && (
        <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: '14px', padding: '40px', textAlign: 'center', color: COLORS.sub }}>
          <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', color: COLORS.accent }} />
          <div style={{ marginTop: '12px', fontSize: '14px' }}>Memuat data job digital...</div>
        </div>
      )}

      {/* ---- View: Job tanpa struk ---- */}
      {report && view === 'noStruk' && datajobOn && (
        <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: '14px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${COLORS.border}`, background: '#fef2f2' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#991b1b', fontWeight: 700, fontSize: '14px' }}>
              <AlertTriangle size={16} /> Job Tanpa Struk (ada di datajob, belum ada struk di POS)
            </div>
            <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '6px' }}>
              Pekerjaan produksi yang sudah tercatat di datajob namun belum punya nomor struk/penjualan di POS — perlu ditindaklanjuti penagihannya.
            </div>
          </div>
          {report.merge.jobTanpaStruk.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.sub, fontSize: '14px' }}>Semua job sudah punya struk. 🎉</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '760px' }}>
                <thead>
                  <tr style={{ background: COLORS.bgSoft, textAlign: 'left', color: COLORS.sub }}>
                    {['No. Job', 'Wilayah', 'Tanggal', 'Pemesan', 'Kategori', 'Oplah', 'Judul'].map((h, i) => (
                      <th key={h} style={{ padding: '11px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', textAlign: i === 5 ? 'right' : 'left', borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.merge.jobTanpaStruk.map((m) => (
                    <tr key={m.jobNo} style={{ borderBottom: `1px solid ${COLORS.bgSoft}` }}>
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 700, color: '#b91c1c', whiteSpace: 'nowrap' }}>{m.jobNo}</td>
                      <td style={{ padding: '10px 14px' }}><span style={{ background: '#eef2ff', color: '#3730a3', fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>{m.wilayah}</span></td>
                      <td style={{ padding: '10px 14px', color: COLORS.sub, whiteSpace: 'nowrap' }}>{m.tanggal || '-'}</td>
                      <td style={{ padding: '10px 14px', color: COLORS.ink }}>{m.customer}</td>
                      <td style={{ padding: '10px 14px', color: COLORS.sub }}>{m.kategori}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: COLORS.ink, whiteSpace: 'nowrap' }}>{fmtNum(m.oplah)}</td>
                      <td style={{ padding: '10px 14px', color: COLORS.ink, maxWidth: '320px' }}>{m.judul || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ---- View: Job tanpa struk (FALLBACK gap detection, datajob belum terhubung) ---- */}
      {report && view === 'noStruk' && !datajobOn && (
        <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: '14px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${COLORS.border}`, background: '#fffbeb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#92400e', fontWeight: 700, fontSize: '14px' }}>
              <AlertTriangle size={16} /> Indikasi Job Tanpa Struk (estimasi celah nomor)
            </div>
            <div style={{ fontSize: '12px', color: '#a16207', marginTop: '6px', lineHeight: 1.5 }}>
              datajob belum terhubung, jadi ini estimasi: nomor job antara{' '}
              <b>{report.gap.min ? 'D' + report.gap.min : '-'}</b> dan <b>{report.gap.max ? 'D' + report.gap.max : '-'}</b>{' '}
              yang tidak muncul di data struk. Hubungkan datajob untuk hasil akurat + wilayah asli.
              {report.gap.capped && ' Daftar dibatasi pada nomor terbaru karena rentang celah sangat lebar.'}
            </div>
          </div>
          {report.gap.missing.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.sub, fontSize: '14px' }}>Tidak ada celah nomor job pada rentang ini.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', padding: '16px' }}>
              {report.gap.missing.map((m) => (
                <div key={m.jobNo} style={{ border: '1px solid #fecaca', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#fef2f2', borderBottom: '1px solid #fee2e2' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '15px', fontWeight: 800, color: '#b91c1c' }}>{m.jobNo}</span>
                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#dc2626', background: '#fff', border: '1px solid #fecaca', borderRadius: '6px', padding: '2px 7px' }}>BELUM ADA STRUK</span>
                  </div>
                  <div style={{ padding: '10px 12px', fontSize: '12px', color: COLORS.sub, lineHeight: 1.6 }}>
                    {m.prev && (
                      <div>
                        <span style={{ color: '#94a3b8' }}>Job sebelumnya: </span>
                        <b style={{ color: COLORS.ink, fontFamily: 'monospace' }}>{m.prev.jobNo}</b>
                        <div style={{ fontSize: '11px' }}>{m.prev.customer} · <span style={{ color: COLORS.accent }}>{m.prev.wilayah}</span> · {m.prev.strukdate}</div>
                      </div>
                    )}
                    {m.next && (
                      <div style={{ marginTop: '6px' }}>
                        <span style={{ color: '#94a3b8' }}>Job sesudahnya: </span>
                        <b style={{ color: COLORS.ink, fontFamily: 'monospace' }}>{m.next.jobNo}</b>
                        <div style={{ fontSize: '11px' }}>{m.next.customer} · <span style={{ color: COLORS.accent }}>{m.next.wilayah}</span> · {m.next.strukdate}</div>
                      </div>
                    )}
                    {!m.prev && !m.next && <span>Tidak ada konteks job di sekitar.</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---- View: Struk tanpa job ---- */}
      {report && view === 'noJob' && datajobOn && (
        <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: '14px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${COLORS.border}`, background: '#fffbeb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#92400e', fontWeight: 700, fontSize: '14px' }}>
              <AlertTriangle size={16} /> Struk Tanpa Job (ada struk di POS, tak ada di datajob)
            </div>
            <div style={{ fontSize: '12px', color: '#a16207', marginTop: '6px' }}>
              Nomor struk/penjualan yang nomor job-nya tidak ditemukan di datajob produksi — kemungkinan salah input nomor job atau job belum tercatat di produksi.
            </div>
          </div>
          {report.merge.strukTanpaJob.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.sub, fontSize: '14px' }}>Semua struk punya job di datajob. 🎉</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '820px' }}>
                <thead>
                  <tr style={{ background: COLORS.bgSoft, textAlign: 'left', color: COLORS.sub }}>
                    {['No. Job', 'No. Struk', 'Tanggal', 'Pemesan', 'Wilayah', 'Kategori', 'Total'].map((h, i) => (
                      <th key={h} style={{ padding: '11px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', textAlign: i === 6 ? 'right' : 'left', borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.merge.strukTanpaJob.map((m) => (
                    <tr key={m.jobNo + m.strukno} style={{ borderBottom: `1px solid ${COLORS.bgSoft}` }}>
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 700, color: '#d97706', whiteSpace: 'nowrap' }}>{m.jobNo}</td>
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: '12px', color: COLORS.ink }}>{m.strukno}</td>
                      <td style={{ padding: '10px 14px', color: COLORS.sub, whiteSpace: 'nowrap' }}>{m.strukdate || '-'}</td>
                      <td style={{ padding: '10px 14px', color: COLORS.ink }}>{m.customer}</td>
                      <td style={{ padding: '10px 14px' }}><span style={{ background: '#eef2ff', color: '#3730a3', fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>{m.wilayah}</span></td>
                      <td style={{ padding: '10px 14px', color: COLORS.sub }}>{m.category}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: COLORS.ink, whiteSpace: 'nowrap' }}>{rupiah(m.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ---- View: Daftar job ---- */}
      {report && view === 'jobs' && (
        <div style={{ background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: '14px', overflow: 'hidden' }}>
          {/* table controls */}
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '7px 12px', flex: '1 1 240px' }}>
              <Search size={15} color={COLORS.sub} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari no job / struk / pemesan / judul..."
                style={{ border: 'none', outline: 'none', fontSize: '13px', width: '100%', color: COLORS.ink }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '7px 12px' }}>
              <Filter size={15} color={COLORS.sub} />
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                style={{ border: 'none', outline: 'none', fontSize: '13px', color: COLORS.ink, background: 'transparent', cursor: 'pointer' }}>
                <option value="ALL">Semua Kategori</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ fontSize: '12px', color: COLORS.sub, marginLeft: 'auto' }}>
              Menampilkan <b>{fmtNum(filteredJobs.length)}</b> job
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '880px' }}>
              <thead>
                <tr style={{ background: COLORS.bgSoft, textAlign: 'left', color: COLORS.sub }}>
                  <th style={{ padding: '11px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', borderBottom: `1px solid ${COLORS.border}` }} title="Sumber: POS (sursys) remarks field">
                    No. Job
                  </th>
                  <th style={{ padding: '11px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', borderBottom: `1px solid ${COLORS.border}` }} title="Sumber: POS (sursys) strukdate field">
                    Tanggal
                  </th>
                  <th style={{ padding: '11px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', borderBottom: `1px solid ${COLORS.border}` }} title="Sumber: POS (sursys) strukno field">
                    No. Struk
                  </th>
                  <th style={{ padding: '11px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', borderBottom: `1px solid ${COLORS.border}` }} title="Sumber: POS (sursys) customername field">
                    Pemesan
                  </th>
                  <th style={{ padding: '11px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', borderBottom: `1px solid ${COLORS.border}` }} title="Sumber: HIJAU = datajob (gapfamily) wilayah field | BIRU = Derived dari customer name">
                    Wilayah ℹ️
                  </th>
                  <th style={{ padding: '11px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', borderBottom: `1px solid ${COLORS.border}` }} title="Sumber: POS (sursys) categorycode field">
                    Kategori
                  </th>
                  <th style={{ padding: '11px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', borderBottom: `1px solid ${COLORS.border}` }} title="Sumber: POS (sursys) remarks field (stripped job number)">
                    Judul Job
                  </th>
                  <th style={{ padding: '11px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', textAlign: 'right', borderBottom: `1px solid ${COLORS.border}` }} title="Sumber: POS (sursys) quantity sum">
                    Qty
                  </th>
                  <th style={{ padding: '11px 14px', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', textAlign: 'right', borderBottom: `1px solid ${COLORS.border}` }} title="Sumber: POS (sursys) totalamount sum">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: '36px', textAlign: 'center', color: COLORS.sub }}>Tidak ada job yang cocok.</td></tr>
                ) : (
                  filteredJobs.map((j) => (
                    <tr key={j.jobNo} style={{ borderBottom: `1px solid ${COLORS.bgSoft}` }} title={`DEBUG: jobNo=${j.jobNo}, wilayahSource=${j.wilayahSource || 'undefined'}, wilayahId=${j.wilayahId || 'N/A'}`}>
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 700, color: COLORS.primary, whiteSpace: 'nowrap' }}>{j.jobNo}</td>
                      <td style={{ padding: '10px 14px', color: COLORS.sub, whiteSpace: 'nowrap' }}>{j.strukdate || '-'}</td>
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: '12px', color: COLORS.ink }}>
                        {j.hasStruk ? j.strukno : (
                          <span style={{ color: '#dc2626', fontWeight: 700 }}>BELUM ADA</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px', color: COLORS.ink }}>{j.customer}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span 
                            style={{ 
                              background: j.wilayahSource === 'datajob' ? '#dcfce7' : '#eef2ff', 
                              color: j.wilayahSource === 'datajob' ? '#166534' : '#3730a3', 
                              fontSize: '11px', 
                              fontWeight: 600, 
                              padding: '3px 8px', 
                              borderRadius: '6px', 
                              whiteSpace: 'nowrap' 
                            }}
                            title={j.wilayahSource === 'datajob' 
                              ? `✅ Wilayah ASLI dari datajob (gapfamily)\nJob: ${j.jobNo}\nWilayah ID: ${j.wilayahId || 'N/A'}` 
                              : `⚠️ Wilayah TURUNAN dari customer name\nCustomer: ${j.customer}\nData produksi tidak ditemukan`
                            }
                          >
                            {j.wilayah}
                          </span>
                          <span 
                            style={{ 
                              fontSize: '10px', 
                              fontWeight: 700,
                              color: j.wilayahSource === 'datajob' ? '#16a34a' : '#6366f1',
                              background: j.wilayahSource === 'datajob' ? '#f0fdf4' : '#eef2ff',
                              padding: '2px 5px',
                              borderRadius: '4px',
                              border: `1px solid ${j.wilayahSource === 'datajob' ? '#bbf7d0' : '#c7d2fe'}`,
                            }}
                            title={j.wilayahSource === 'datajob' ? 'Data dari sistem produksi (datajob)' : 'Data diturunkan dari nama customer'}
                          >
                            {j.wilayahSource === 'datajob' ? 'DB' : 'AUTO'}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ background: '#fff7ed', color: COLORS.primary, fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>{j.category}</span>
                      </td>
                      <td style={{ padding: '10px 14px', color: COLORS.ink, maxWidth: '320px' }}>{j.judul || '-'}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: COLORS.ink, whiteSpace: 'nowrap' }}>{fmtNum(j.qty)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: COLORS.ink, whiteSpace: 'nowrap' }}>{rupiah(j.total)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* unmatched note */}
      {report && view === 'jobs' && report.unmatched.length > 0 && (
        <div style={{ marginTop: '12px', fontSize: '12px', color: COLORS.sub }}>
          Catatan: {fmtNum(report.unmatched.length)} baris transaksi tidak memiliki nomor job berformat "D" pada remarks dan tidak ditampilkan di tabel job.
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default DigitalJobReport;
